import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as SayifyAPI from '../../lib/sayifyAPI';

/**
 * OnboardingGuard - Redirects users to onboarding if not completed
 * 
 * Wraps components that should only be accessible after onboarding is complete.
 * Checks onboarding status on mount and redirects to /onboarding if needed.
 */
const OnboardingGuard = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [checking, setChecking] = useState(true);
    const [onboardingComplete, setOnboardingComplete] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        const checkOnboardingStatus = async () => {
            // Skip check if already inside the first-run flow
            if (location.pathname.startsWith('/start')) {
                setChecking(false);
                setOnboardingComplete(true);
                return;
            }

            try {
                const status = await SayifyAPI.getOnboardingStatus(controller.signal);

                // Check if onboarding is completed or skipped
                const isComplete = status.onboarding_completed || status.onboarding_skipped;

                if (!isComplete) {
                    // Send first-run users into /start
                    navigate('/start', { replace: true });
                } else {
                    setOnboardingComplete(true);
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Failed to check onboarding status:', err);
                    // On error, allow access (fail open to avoid blocking users)
                    setOnboardingComplete(true);
                }
            } finally {
                setChecking(false);
            }
        };

        checkOnboardingStatus();

        return () => controller.abort();
    }, [navigate, location.pathname]);

    // Show loading state while checking
    if (checking) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                color: '#6b7280'
            }}>
                Loading...
            </div>
        );
    }

    // Render children if onboarding is complete
    return onboardingComplete ? children : null;
};

export default OnboardingGuard;
